import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditLogsTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [dateRange, setDateRange] = useState('7');

  const auditLogs = [
    {
      id: 1,
      timestamp: "2025-09-19 11:25:30",
      user: "Sarah Johnson",
      userRole: "Senior Analyst",
      action: "User Login",
      resource: "Authentication System",
      details: "Successful login from IP 192.168.1.45",
      severity: "Info",
      ipAddress: "192.168.1.45",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    },
    {
      id: 2,
      timestamp: "2025-09-19 11:20:15",
      user: "Michael Chen",
      userRole: "SOC Manager",
      action: "Configuration Change",
      resource: "SSL Certificate",
      details: "Updated SSL certificate for primary domain",
      severity: "Warning",
      ipAddress: "192.168.1.32",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    },
    {
      id: 3,
      timestamp: "2025-09-19 11:15:42",
      user: "System",
      userRole: "System",
      action: "Database Backup",
      resource: "Primary Database",
      details: "Automated backup completed successfully (2.4 GB)",
      severity: "Info",
      ipAddress: "127.0.0.1",
      userAgent: "System Process"
    },
    {
      id: 4,
      timestamp: "2025-09-19 11:10:28",
      user: "Emily Rodriguez",
      userRole: "Compliance Officer",
      action: "Data Export",
      resource: "Incident Reports",
      details: "Exported incident data for compliance audit (Q3 2025)",
      severity: "Info",
      ipAddress: "192.168.1.67",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    },
    {
      id: 5,
      timestamp: "2025-09-19 11:05:12",
      user: "David Thompson",
      userRole: "Junior Analyst",
      action: "Failed Login",
      resource: "Authentication System",
      details: "Failed login attempt - invalid password",
      severity: "Warning",
      ipAddress: "192.168.1.89",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    },
    {
      id: 6,
      timestamp: "2025-09-19 11:00:55",
      user: "Admin",
      userRole: "Administrator",
      action: "User Creation",
      resource: "User Management",
      details: "Created new user account: john.doe@cyberguard.gov",
      severity: "Info",
      ipAddress: "192.168.1.10",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    },
    {
      id: 7,
      timestamp: "2025-09-19 10:55:33",
      user: "System",
      userRole: "System",
      action: "Integration Sync",
      resource: "Active Directory",
      details: "Synchronized user data from Active Directory (245 users)",
      severity: "Info",
      ipAddress: "127.0.0.1",
      userAgent: "System Process"
    },
    {
      id: 8,
      timestamp: "2025-09-19 10:50:18",
      user: "Sarah Johnson",
      userRole: "Senior Analyst",
      action: "Incident Update",
      resource: "Incident #INC-2025-0892",
      details: "Updated incident status from \'In Progress\' to \'Resolved'",
      severity: "Info",
      ipAddress: "192.168.1.45",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    }
  ];

  const actionTypes = [
    { value: '', label: 'All Actions' },
    { value: 'login', label: 'User Login' },
    { value: 'logout', label: 'User Logout' },
    { value: 'config_change', label: 'Configuration Change' },
    { value: 'user_creation', label: 'User Creation' },
    { value: 'user_deletion', label: 'User Deletion' },
    { value: 'data_export', label: 'Data Export' },
    { value: 'backup', label: 'Database Backup' },
    { value: 'integration_sync', label: 'Integration Sync' },
    { value: 'incident_update', label: 'Incident Update' }
  ];

  const users = [
    { value: '', label: 'All Users' },
    { value: 'sarah.johnson', label: 'Sarah Johnson' },
    { value: 'michael.chen', label: 'Michael Chen' },
    { value: 'emily.rodriguez', label: 'Emily Rodriguez' },
    { value: 'david.thompson', label: 'David Thompson' },
    { value: 'admin', label: 'Admin' },
    { value: 'system', label: 'System' }
  ];

  const dateRanges = [
    { value: '1', label: 'Last 24 Hours' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const filteredLogs = auditLogs?.filter(log => {
    const matchesSearch = log?.details?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.user?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesAction = !selectedAction || log?.action?.toLowerCase()?.includes(selectedAction);
    const matchesUser = !selectedUser || log?.user?.toLowerCase()?.includes(selectedUser);
    return matchesSearch && matchesAction && matchesUser;
  });

  const getSeverityBadge = (severity) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (severity) {
      case 'Info':
        return `${baseClasses} bg-success text-success-foreground`;
      case 'Warning':
        return `${baseClasses} bg-warning text-warning-foreground`;
      case 'Error':
        return `${baseClasses} bg-error text-error-foreground`;
      case 'Critical':
        return `${baseClasses} bg-destructive text-destructive-foreground`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'User Login':
        return 'LogIn';
      case 'User Logout':
        return 'LogOut';
      case 'Configuration Change':
        return 'Settings';
      case 'User Creation':
        return 'UserPlus';
      case 'User Deletion':
        return 'UserMinus';
      case 'Data Export':
        return 'Download';
      case 'Database Backup':
        return 'HardDrive';
      case 'Integration Sync':
        return 'RefreshCw';
      case 'Incident Update':
        return 'FileText';
      case 'Failed Login':
        return 'AlertTriangle';
      default:
        return 'Activity';
    }
  };

  const handleExportLogs = () => {
    console.log('Exporting audit logs...');
  };

  const handleViewDetails = (logId) => {
    console.log('Viewing details for log:', logId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="FileText" size={20} className="mr-2" />
            Audit Logs
          </h3>
          <p className="text-sm text-muted-foreground">Comprehensive system activity and security audit trail</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" iconName="RefreshCw" iconPosition="left">
            Refresh
          </Button>
          <Button variant="default" iconName="Download" iconPosition="left" onClick={handleExportLogs}>
            Export Logs
          </Button>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            type="search"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
          <Select
            placeholder="Filter by action"
            options={actionTypes}
            value={selectedAction}
            onChange={setSelectedAction}
          />
          <Select
            placeholder="Filter by user"
            options={users}
            value={selectedUser}
            onChange={setSelectedUser}
          />
          <Select
            placeholder="Date range"
            options={dateRanges}
            value={dateRange}
            onChange={setDateRange}
          />
        </div>
      </div>
      {/* Audit Logs Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {filteredLogs?.length} log entries found
            </span>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={14} />
              <span>Real-time monitoring active</span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-foreground">Timestamp</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">User</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Action</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Resource</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Details</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Severity</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs?.map((log) => (
                <tr key={log?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {new Date(log.timestamp)?.toLocaleDateString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(log.timestamp)?.toLocaleTimeString()}
                      </p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="User" size={12} color="white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{log?.user}</p>
                        <p className="text-xs text-muted-foreground">{log?.userRole}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name={getActionIcon(log?.action)} size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{log?.action}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-foreground">{log?.resource}</td>
                  <td className="p-4">
                    <p className="text-sm text-foreground max-w-xs truncate" title={log?.details}>
                      {log?.details}
                    </p>
                    <p className="text-xs text-muted-foreground">IP: {log?.ipAddress}</p>
                  </td>
                  <td className="p-4">
                    <span className={getSeverityBadge(log?.severity)}>
                      {log?.severity}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="Eye"
                        onClick={() => handleViewDetails(log?.id)}
                      />
                      <Button variant="ghost" size="sm" iconName="Copy" />
                      <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-border">
          <span className="text-sm text-muted-foreground">
            Showing 1-{filteredLogs?.length} of {filteredLogs?.length} entries
          </span>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="ChevronLeft" disabled />
            <Button variant="outline" size="sm">1</Button>
            <Button variant="outline" size="sm" iconName="ChevronRight" disabled />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogsTab;